import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { confirmDialog } from "primereact/confirmdialog";
import { Dropdown } from "primereact/dropdown";
import { Tag } from "primereact/tag";
import { ToggleButton } from "primereact/togglebutton";
import { useMemo } from "react";
import {
  useUpdateContractHierarchyRuleMutation,
  useDeleteContractHierarchyRuleMutation,
} from "store/queries/contractHierarchyRules";
import styled from "styled-components";
import {
  IContractHierarchyRule,
  IContractHierarchyRuleSuppression,
  ISuppressionType,
  ITriggerRulesetType,
} from "types/ContractHierarchyRule/ContractHierarchyRule";

interface IRulesetOption {
  label: string;
  value: string;
  rulesetType: ITriggerRulesetType;
}

interface IHierarchyRuleCardProps {
  rule: IContractHierarchyRule;
  allRules: IContractHierarchyRule[];
  rulesetOptions: IRulesetOption[];
}

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const SuppressionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
`;

const SuppressionList = styled.div`
  margin-top: 8px;
`;

const suppressionTypeOptions = [
  { label: "Not Paid (100% discount)", value: "NOT_PAID" },
  { label: "Pay Both (remove adjustment)", value: "PAY_BOTH" },
];

export function HierarchyRuleCard({
  rule,
  allRules,
  rulesetOptions,
}: IHierarchyRuleCardProps) {
  const [updateRule] = useUpdateContractHierarchyRuleMutation();
  const [deleteRule] = useDeleteContractHierarchyRuleMutation();

  const resolveRulesetName = (rulesetId: string): string => {
    const option = rulesetOptions.find((o) => o.value === rulesetId);
    return option?.label || rulesetId;
  };

  const handleToggleActive = () => {
    updateRule({ id: rule.id, active: !rule.active });
  };

  const handleDeleteRule = () => {
    confirmDialog({
      message: `Delete hierarchy rule "${rule.name}"?`,
      header: "Confirm Delete",
      icon: "pi pi-exclamation-triangle",
      accept: () => deleteRule(rule.id),
    });
  };

  const handleSuppressionTypeChange = (
    index: number,
    newType: ISuppressionType
  ) => {
    const updated = [...rule.suppressions];
    updated[index] = { ...updated[index], suppressionType: newType };
    updateRule({ id: rule.id, suppressions: updated });
  };

  const handleRemoveSuppression = (index: number) => {
    const updated = rule.suppressions.filter((_, i) => i !== index);
    updateRule({ id: rule.id, suppressions: updated });
  };

  const handleAddSuppression = (rulesetId: string) => {
    const option = rulesetOptions.find((o) => o.value === rulesetId);
    if (!option) return;

    const newSuppression: IContractHierarchyRuleSuppression = {
      rulesetType: option.rulesetType,
      rulesetId: option.value,
      suppressionType: "NOT_PAID",
    };
    updateRule({
      id: rule.id,
      suppressions: [...rule.suppressions, newSuppression],
    });
  };

  // Compute "suppressed by" — other rules whose suppressions reference this rule's trigger ruleset
  const suppressedBy = useMemo(() => {
    return allRules.filter(
      (other) =>
        other.id !== rule.id &&
        other.suppressions.some(
          (sup) => sup.rulesetId === rule.triggerRulesetId
        )
    );
  }, [allRules, rule]);

  // Available rulesets to add as suppressions (exclude already added)
  const availableForSuppression = useMemo(() => {
    const existingIds = new Set(rule.suppressions.map((s) => s.rulesetId));
    return rulesetOptions.filter((o) => !existingIds.has(o.value));
  }, [rulesetOptions, rule.suppressions]);

  const cardTitle = (
    <CardHeader>
      <div className="flex align-items-center gap-2">
        <span className="font-bold">{rule.name}</span>
        <Tag value={`Priority ${rule.priority}`} severity="info" />
        <Tag
          value={rule.triggerRulesetType}
          severity="warning"
          className="text-xs"
        />
      </div>
      <div className="flex align-items-center gap-2">
        <ToggleButton
          checked={rule.active}
          onChange={handleToggleActive}
          onLabel="Active"
          offLabel="Inactive"
          onIcon="pi pi-check"
          offIcon="pi pi-times"
          className="w-8rem"
        />
        <Button
          icon="pi pi-trash"
          className="p-button-danger p-button-text"
          onClick={handleDeleteRule}
          tooltip="Delete rule"
        />
      </div>
    </CardHeader>
  );

  return (
    <Card title={cardTitle} className="mb-3">
      <div className="grid">
        <div className="col-6">
          <h4 className="mt-0 mb-2">Suppresses</h4>
          <SuppressionList>
            {rule.suppressions.map((sup, idx) => (
              <SuppressionRow key={`${sup.rulesetId}-${idx}`}>
                <i
                  className="pi pi-circle-fill"
                  style={{
                    color:
                      sup.suppressionType === "NOT_PAID"
                        ? "#ef4444"
                        : "#22c55e",
                    fontSize: "8px",
                  }}
                />
                <span className="flex-grow-1">
                  {resolveRulesetName(sup.rulesetId)}
                </span>
                <Dropdown
                  value={sup.suppressionType}
                  options={suppressionTypeOptions}
                  onChange={(e) => handleSuppressionTypeChange(idx, e.value)}
                  className="w-21rem"
                  placeholder="Suppression type"
                />
                <Button
                  icon="pi pi-times"
                  className="p-button-text p-button-sm p-button-danger"
                  onClick={() => handleRemoveSuppression(idx)}
                />
              </SuppressionRow>
            ))}
            {availableForSuppression.length > 0 && (
              <Dropdown
                value={null}
                options={availableForSuppression}
                onChange={(e) => handleAddSuppression(e.value)}
                placeholder="+ Add Suppression"
                className="w-full mt-2"
              />
            )}
          </SuppressionList>
        </div>

        <div className="col-6">
          <h4 className="mt-0 mb-2">Suppressed By</h4>
          {suppressedBy.length === 0 ? (
            <span className="text-500">
              No other rules suppress this trigger.
            </span>
          ) : (
            suppressedBy.map((other) => (
              <div key={other.id} className="mb-1">
                <Tag value={other.name} severity="info" className="mr-1" />
                <span className="text-sm text-500">
                  Priority {other.priority}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </Card>
  );
}
